/**
 * Top-level planner entry — fixed-point loop per `04-` §16.
 *
 * Pure function `plan_unmemoized(snapshot, rootTaskId?, inclusive?): PlanResult`.
 *
 * Algorithm:
 *   1. Build SchedulingGraph (memoized in core/api.ts).
 *   2. Cycle short-circuit (Part I §5 — no partial plans): findCycles on the
 *      graph; if any, return PlanResult{ conflicts:[{kind:"cycle",...}],
 *      plans: empty, iterations:0, converged:true, order:[] }.
 *   3. selectComponent(graph, rootTaskId, inclusive) when rootTaskId given;
 *      else the full graph is in scope.
 *   4. plan_0 — initial PlanState built from anchor dates.
 *   5. For i in 1..maxIterations:
 *        plan_i = scheduleOnce(graph, plan_{i-1}, config, scope)
 *        if plansEqual(plan_i, plan_{i-1}) → converged.
 *   6. PlanResult emits only entries that differ from the snapshot's task
 *      dates (sparse output per `04-` §A.7) and includes the toposort
 *      `order` so the adapter can fire `onAfterTaskAutoSchedule` in the
 *      planner's resolution order.
 */

import type {
  Conflict,
  NormalizedTaskID,
  PlanResult,
  ScheduledDates,
  ScheduleReason,
  Snapshot,
  TaskID
} from "../types";
import { normalizeTaskId } from "../ids";
import { buildSchedulingGraph, findCycles } from "../api";
import { scheduleOnce, type PerNodePlan, type PlanState } from "./schedule_pass";
import { selectComponent, selectPredecessorClosure } from "./connected";
import { resolveConstraintType } from "./constraints";

export function plan_unmemoized(
  snapshot: Snapshot,
  rootTaskId?: TaskID,
  inclusive: boolean = true
): PlanResult {
  const graph = buildSchedulingGraph(snapshot);

  // Part I §5: short-circuit on cycles. Engine emits no partial plan.
  const cycles = findCycles(graph);
  if (cycles.length > 0) {
    const conflicts: Conflict[] = cycles.map(c => ({
      kind: "cycle" as const,
      taskIds: c.tasks,
      linkIds: c.links
    }));
    return {
      plans: new Map(),
      iterations: 0,
      converged: true,
      conflicts,
      order: []
    };
  }

  // Optional component scoping per D-02.
  //
  // When `rootTaskId` is a SUMMARY task that's been filtered from the
  // scheduling graph (e.g., `gap_behavior:"compress"` collapses moveProjects
  // to false, so every summary is dropped from `graph.nodes`), the bare
  // `selectComponent(graph, summaryId, ...)` returns an empty scope and
  // the planner does nothing. v1's `linksBuilder.getLinkedTasks(summaryId,
  // true)` walks both the summary's `$source`/`$target` links AND iterates
  // its children, collecting every linked task in the subtree. Mirror that
  // by treating the summary's descendants as additional roots — union the
  // scope of every in-graph descendant.
  //
  // Test 11_auto_scheduling/017_finish_to_start.js exercises this:
  // `gantt.addLink({source: 1, target: 2})` triggers
  // `autoScheduleViaEngine(gantt, 1, true)` where task 1 is a project
  // summary; under compress the link expands to leaf-to-leaf edges, so the
  // planner has work to do — but only if the leaves are in scope.
  let scope: ReadonlySet<NormalizedTaskID> | undefined;
  let normalizedRootTaskId: NormalizedTaskID | undefined;
  if (rootTaskId !== undefined) {
    normalizedRootTaskId = normalizeTaskId(rootTaskId);
    const sel = selectComponent(graph, normalizedRootTaskId, inclusive);
    scope = sel.nodes;
    if (scope.size === 0 && snapshot.hierarchy.isSummary(normalizedRootTaskId)) {
      const expanded = new Set<NormalizedTaskID>();
      for (const desc of snapshot.hierarchy.descendantsOf(normalizedRootTaskId)) {
        if (!graph.nodes.has(desc)) continue;
        const subScope = selectComponent(graph, desc, inclusive);
        for (const id of subScope.nodes) expanded.add(id);
      }
      scope = expanded;
    }

    // GS-1771 multilevel (retest 088): a scoped reschedule of a deep LEAF must
    // propagate UP through containment. Dragging a leaf changes its ancestor
    // summaries' aggregate dates, and an ancestor may carry a cross-summary
    // scheduling link (e.g. project #2 → project #4) whose target must then
    // reschedule. `selectComponent` only walks scheduling edges, so the bare
    // component of a leaf never reaches a sibling project linked to its parent —
    // the planner recomputed the parent's end but left the linked sibling seeded
    // from the stale `previousPlan` (project #4 didn't move).
    //
    // Bring the root's ANCESTOR summaries into scope and union the scheduling
    // component of each, so a project linked to an ancestor (and, via the GS-984
    // expansion below, that project's descendants) comes along. The ancestors
    // themselves are recorded in `rootAncestorSummaries` and EXCLUDED from the
    // GS-984 descendant expansion: a pure ancestor only needs to re-aggregate,
    // NOT drag its other children (a common ancestor like the top project would
    // otherwise pull in unrelated sibling sub-projects — over-expanding the drag
    // into a full reschedule).
    const rootAncestorSummaries = new Set<NormalizedTaskID>();
    if (scope && scope.size > 0) {
      const ancExpanded = new Set<NormalizedTaskID>(scope);
      for (const id of scope) {
        for (const anc of snapshot.hierarchy.ancestorsOf(id)) {
          if (!graph.nodes.has(anc) || !snapshot.hierarchy.isSummary(anc)) continue;
          rootAncestorSummaries.add(anc);
          if (ancExpanded.has(anc)) continue;
          const ancComp = selectComponent(graph, anc, inclusive);
          for (const n of ancComp.nodes) ancExpanded.add(n);
        }
      }
      scope = ancExpanded;
    }

    // GS-984: when a scoped pass reaches a SUMMARY through a scheduling edge
    // (e.g. `gantt.autoSchedule(leafId)` where the leaf has an SF/FS/FF/SS
    // link to a project), the summary's descendants must also be in scope.
    //
    // The summary is scheduled as a block: `summary_translation.applyTranslation`
    // writes every descendant by `delta = newAnchor − oldAnchor`. When the
    // descendant is OUT of scope, the next iteration's `scheduleOnce` seeds
    // it back from `previousPlan` (i.e. the just-translated date), and
    // `summaryBounds` reads that translated position into the next iteration's
    // `effBounds`. The summary's effective span changes between iterations,
    // shifting the SF/FF candidate, which shifts the delta, which shifts the
    // descendants again — never converging.
    //
    // Concretely on the GS-984 sample (link 12 SF 13, project 13 contains 14
    // with SNET, 15 with no constraint): scoped `autoSchedule(12)` oscillates
    // descendant #14 between 08-03 → 09-03 (odd iters) and 12-03 → 13-03
    // (even iters), exhausting maxIterations without converging. The full
    // `autoSchedule()` doesn't show this because descendants are in scope
    // and re-resolved fresh from their anchors every iteration.
    //
    // Fix: when an in-scope summary has descendants, run `selectComponent`
    // from each descendant and union the result — same pattern as the
    // filtered-summary branch above. This brings cross-component-linked
    // siblings of the descendants along too, matching v1's
    // `linksBuilder.getLinkedTasks` walk.
    if (scope && scope.size > 0) {
      let hasInScopeSummary = false;
      for (const id of scope) {
        if (snapshot.hierarchy.isSummary(id)) { hasInScopeSummary = true; break; }
      }
      if (hasInScopeSummary) {
        const expanded = new Set<NormalizedTaskID>(scope);
        for (const id of scope) {
          if (!snapshot.hierarchy.isSummary(id)) continue;
          // Skip the root's pure ancestors — they only re-aggregate; expanding
          // their descendants would drag in unrelated sibling sub-projects.
          if (rootAncestorSummaries.has(id)) continue;
          for (const desc of snapshot.hierarchy.descendantsOf(id)) {
            if (!graph.nodes.has(desc)) continue;
            if (expanded.has(desc)) continue;
            const subScope = selectComponent(graph, desc, inclusive);
            for (const newId of subScope.nodes) expanded.add(newId);
          }
        }
        scope = expanded;
      }
    }
  }

  // Upstream boundary of the scoped reschedule. The inclusive scope drags the
  // root's predecessors in only to BOUND it (provide their end dates); v1 never
  // re-resolves them. Pass the predecessor-closure so `schedule_pass` gives them
  // the same anchor-floor treatment as the root under `gap_behavior:"compress"`,
  // preventing a downstream drag from compressing a predecessor the user placed
  // (retest 030). Computed only for an inclusive scoped pass; the closure is
  // empty when the root is a summary filtered from the graph (compress), so the
  // summary-root expansion paths above are unaffected.
  let rootPredecessorIds: ReadonlySet<NormalizedTaskID> | undefined;
  if (normalizedRootTaskId !== undefined && inclusive && scope) {
    const upstream = selectPredecessorClosure(graph, normalizedRootTaskId);
    if (upstream.size > 0) {
      const inScope = new Set<NormalizedTaskID>();
      for (const id of upstream) if (scope.has(id)) inScope.add(id);
      rootPredecessorIds = inScope;
    }
  }

  // plan_0: anchor-based starting state. Every in-scope node seeded with its
  // anchor dates so the first iteration of `scheduleOnce` has predecessor
  // ends / successor starts to read.
  let plan_prev: PlanState = buildInitialPlan(graph, scope);
  let order: readonly NormalizedTaskID[] = [];
  let conflicts: readonly Conflict[] = [];
  let converged = false;
  let iterations = 0;

  const maxIterations = Math.max(1, snapshot.config.maxIterations | 0);

  for (let i = 1; i <= maxIterations; i++) {
    iterations = i;
    const r = scheduleOnce({
      graph,
      previousPlan: plan_prev,
      config: snapshot.config,
      scope,
      projectStartTs: snapshot.projectStart.getTime(),
      explicitProjectStartTs: snapshot.explicitProjectStart?.getTime() || undefined,
      projectEndTs: snapshot.projectEnd.getTime(),
      explicitProjectEndTs: snapshot.explicitProjectEnd?.getTime() || undefined,
      rootTaskId: normalizedRootTaskId,
      rootPredecessorIds
    });
    if (plansEqual(plan_prev, r.plan)) {
      // Converged on this iteration — the prior pass produced the same plan.
      converged = true;
      plan_prev = r.plan;
      order = r.order;
      conflicts = r.conflicts;
      break;
    }
    plan_prev = r.plan;
    order = r.order;
    conflicts = r.conflicts;
  }
  // If we hit maxIterations without converging, leave converged=false so the
  // adapter can fire `onAutoScheduleNoConverge`.

  // Build the sparse plans map: include only tasks whose dates differ from
  // the snapshot's anchors. (Excluded nodes have anchor === plan ⇒ omitted.)
  const plans = new Map<NormalizedTaskID, ScheduledDates>();
  for (const [id, entry] of plan_prev) {
    const task = snapshot.tasks.get(id);
    if (!task) continue;
    const snapStart = task.startDate.getTime();
    const snapEnd = task.endDate.getTime();
    if (entry.startTs === snapStart && entry.endTs === snapEnd) continue;

    const resolvedConstraint = resolveConstraintType(snapshot, id);
    plans.set(id, {
      start: new Date(entry.startTs),
      end: new Date(entry.endTs),
      resolvedConstraint,
      reason: entry.reason
    });
  }

  return {
    plans,
    iterations,
    converged,
    conflicts,
    order
  };
}

/**
 * Seed plan_0 from the graph's anchor dates. Excluded nodes (D-05 dragged
 * or `auto_scheduling: false`) carry their anchor as their final plan — the
 * scheduling-pass code respects that by re-emitting the anchor for them.
 */
function buildInitialPlan(
  graph: ReturnType<typeof buildSchedulingGraph>,
  scope: ReadonlySet<NormalizedTaskID> | undefined
): PlanState {
  const plan = new Map<NormalizedTaskID, PerNodePlan>();
  const defaultReason: ScheduleReason = { kind: "preferred" };
  for (const [id, node] of graph.nodes) {
    if (scope && !scope.has(id)) continue;
    plan.set(id, {
      startTs: node.anchorStartTs,
      endTs: node.anchorEndTs,
      reason: defaultReason
    });
  }
  return plan;
}

/**
 * Deep-equal comparison on the maps' (start, end) tuples. Reason is NOT
 * part of equality — it's a label, not a scheduling decision. Two passes
 * that produce identical dates with different reasons are considered
 * converged.
 */
function plansEqual(a: PlanState, b: PlanState): boolean {
  if (a.size !== b.size) return false;
  for (const [id, entry] of a) {
    const other = b.get(id);
    if (!other) return false;
    if (entry.startTs !== other.startTs) return false;
    if (entry.endTs !== other.endTs) return false;
  }
  return true;
}
